/* eslint-disable */
import { useState, useEffect, useRef } from "react";
import { C, t, fmt$, formatDate } from "../shared/globals.js";

function PriceTradeChart({ entries, currentPrice }) {
  const svgRef  = useRef(null);
  const wrapRef = useRef(null);
  const lineRef = useRef(null);
  const [dims, setDims]       = useState({ w: 400, h: 200 });
  const [tooltip, setTooltip] = useState(null);
  const [animated, setAnimated] = useState(false);
  const [dotMode, setDotMode] = useState("dollars"); // "dollars" | "shares"

  useEffect(() => {
    const obs = new ResizeObserver(es => {
      const { width, height } = es[0].contentRect;
      setDims({ w: Math.max(width, 100), h: Math.max(height, 80) });
    });
    if (wrapRef.current) {
      obs.observe(wrapRef.current);
      requestAnimationFrame(() => {
        if (wrapRef.current) {
          const { width } = wrapRef.current.getBoundingClientRect();
          if (width > 0) setDims(d => ({ ...d, w: width }));
        }
      });
    }
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    setAnimated(false);
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, [entries.length, currentPrice]);

  useEffect(() => {
    if (!animated || !lineRef.current) return;
    const el = lineRef.current;
    const len = el.getTotalLength?.() || 3000;
    el.style.transition = "none";
    el.style.strokeDasharray = `${len}`;
    el.style.strokeDashoffset = `${len}`;
    el.style.opacity = "0";
    requestAnimationFrame(() => {
      setTimeout(() => {
        el.style.transition = "stroke-dashoffset 1600ms cubic-bezier(0.4,0,0.2,1), opacity 200ms ease";
        el.style.strokeDashoffset = "0";
        el.style.opacity = "1";
      }, 80);
    });
  }, [animated, dims]);

  if (!entries.length || !currentPrice) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--text5)", fontFamily:"Montserrat,sans-serif", fontSize:11, letterSpacing:"0.2em" }}>
      ENTER PRICE
    </div>
  );

  // Build a buy-price map by date (sum shares per day)
  const buyMap = {};
  entries.forEach(e => {
    if (!buyMap[e.date]) buyMap[e.date] = { shares: 0, dollars: 0, count: 0, buyPrice: e.dollars / e.shares };
    buyMap[e.date].shares  += e.shares;
    buyMap[e.date].dollars += e.dollars;
    buyMap[e.date].count   += 1;
    buyMap[e.date].buyPrice = buyMap[e.date].dollars / buyMap[e.date].shares;
  });

  // Build daily price series from first entry to today using buy prices + current price
  const sorted   = [...entries].sort((a, b) => a.date.localeCompare(b.date));
  const firstDate = new Date(sorted[0].date);
  const today     = new Date();
  const days = [];
  for (let d = new Date(firstDate); d <= today; d.setDate(d.getDate() + 1)) {
    const iso = d.toISOString().split("T")[0];
    days.push(iso);
  }

  // For each day, find the closest known buy price or interpolate
  // Strategy: carry forward the last known buy price, end at currentPrice
  const knownPrices = sorted.map(e => ({ date: e.date, price: e.dollars / e.shares }));
  const priceByDate = {};
  knownPrices.forEach(p => { priceByDate[p.date] = p.price; });

  // Build smooth series: interpolate between known points + currentPrice at end
  const anchors = [...knownPrices, { date: today.toISOString().split("T")[0], price: currentPrice }];
  const series = days.map(iso => {
    if (priceByDate[iso]) return { date: iso, price: priceByDate[iso] };
    // Find surrounding anchors
    const prev = anchors.filter(a => a.date <= iso).pop();
    const next = anchors.find(a => a.date > iso);
    if (!prev) return { date: iso, price: next?.price || currentPrice };
    if (!next) return { date: iso, price: prev.price };
    const t = (iso.localeCompare(prev.date)) / (next.date.localeCompare(prev.date));
    return { date: iso, price: prev.price + (next.price - prev.price) * t };
  });

  const PAD = { t:24, r:24, b:38, l:64 };
  const W = dims.w, H = dims.h;
  const gW = W - PAD.l - PAD.r, gH = H - PAD.t - PAD.b;
  if (gW <= 0 || gH <= 0) return null;

  const prices = series.map(p => p.price);
  const minP   = Math.min(...prices) * 0.93;
  const maxP   = Math.max(...prices) * 1.06;
  const rangeP = maxP - minP || 1;

  const xS = i => PAD.l + (i / Math.max(series.length - 1, 1)) * gW;
  const yS = v => PAD.t + gH - ((v - minP) / rangeP) * gH;

  // Build SVG path
  let pathD = `M ${xS(0)} ${yS(series[0].price)}`;
  for (let i = 1; i < series.length; i++) {
    const cpx = (xS(i-1) + xS(i)) / 2;
    pathD += ` C ${cpx} ${yS(series[i-1].price)}, ${cpx} ${yS(series[i].price)}, ${xS(i)} ${yS(series[i].price)}`;
  }
  const areaPath = pathD + ` L ${xS(series.length-1)} ${PAD.t+gH} L ${xS(0)} ${PAD.t+gH} Z`;

  // Buy dots
  const buyDots = series.map((p, i) => buyMap[p.date] ? { i, x: xS(i), y: yS(p.price), data: buyMap[p.date], date: p.date } : null).filter(Boolean);

  const yTicks  = Array.from({length: 5}, (_, i) => minP + (rangeP / 4) * i);
  const xIdxs   = [0, Math.floor((series.length - 1) / 2), series.length - 1];

  const onMouseMove = e => {
    const rect = svgRef.current?.getBoundingClientRect();
    if (!rect) return;
    const rawX = e.clientX - rect.left - PAD.l;
    const idx  = Math.max(0, Math.min(series.length - 1, Math.round((rawX / gW) * (series.length - 1))));
    // Check if near a buy dot (within 12px)
    const nearDot = buyDots.find(d => Math.abs(d.x - xS(idx)) < 12);
    if (nearDot) {
      setTooltip({ x: nearDot.x, y: nearDot.y, isBuy: true, data: nearDot.data, date: nearDot.date, price: series[nearDot.i].price });
    } else {
      const p = series[idx];
      setTooltip({ x: xS(idx), y: yS(p.price), isBuy: false, date: p.date, price: p.price });
    }
  };

  const isUp = currentPrice >= (series[0]?.price || currentPrice);
  const lineColor = isUp ? "#5aad27" : C.red;
  const greyLine = "#3a3a3a";
  const greyArea = "#2a2a2a";

  return (
    <div ref={wrapRef} style={{ position:"relative", width:"100%", height:"100%" }}>
      {/* Toggle buttons */}
      <div style={{ position:"absolute", top:0, right:0, display:"flex", gap:6, zIndex:5 }}>
        {[{ key:"dollars", label:"$ AMOUNT" }, { key:"shares", label:"АКЦИИ" }].map(({ key, label }) => (
          <button key={key} onClick={() => setDotMode(key)} style={{
            background: dotMode === key ? "rgba(242,135,5,0.15)" : "transparent",
            border: `1px solid ${dotMode === key ? C.orange : "#2a2a2a"}`,
            color: dotMode === key ? C.orange : "#555",
            borderRadius:6, padding:"3px 10px", cursor:"pointer",
            fontFamily:"Montserrat,sans-serif", fontSize:9, fontWeight:700, letterSpacing:"0.1em",
            transition:"all 0.15s",
          }}>{label}</button>
        ))}
      </div>
      <svg ref={svgRef} width={W} height={H}
        onMouseMove={onMouseMove} onMouseLeave={() => setTooltip(null)}
        style={{ display:"block", overflow:"visible" }}>
        {yTicks.map((v, i) => (
          <line key={i} x1={PAD.l} y1={yS(v)} x2={W - PAD.r} y2={yS(v)} stroke="var(--border)" strokeWidth="1"/>
        ))}
        {yTicks.map((v, i) => (
          <text key={i} x={PAD.l - 8} y={yS(v) + 4} textAnchor="end" fill="var(--text5)"
            fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
            {v >= 1000 ? `$${(v/1000).toFixed(0)}k` : `$${v.toFixed(0)}`}
          </text>
        ))}
        {xIdxs.map(idx => (
          <text key={idx} x={xS(idx)} y={H - 6} textAnchor="middle" fill="var(--text5)"
            fontSize="9" fontFamily="Montserrat,sans-serif" fontWeight="700">
            {formatDate(series[idx].date)}
          </text>
        ))}

        {/* Area + line */}
        <path ref={lineRef} d={pathD} fill="none" stroke={greyLine} strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round"
          style={{ opacity: 0 }}/>

        {/* Buy dots */}
        {buyDots.map((dot, i) => {
          const dotColor = currentPrice >= dot.data.buyPrice ? "#5aad27" : C.orange;
          const isHovered = tooltip?.isBuy && tooltip.x === dot.x && tooltip.y === dot.y;
          const allVals = buyDots.map(d => dotMode === "dollars" ? d.data.dollars : d.data.shares);
          const minV = Math.min(...allVals);
          const maxV = Math.max(...allVals);
          const val = dotMode === "dollars" ? dot.data.dollars : dot.data.shares;
          const tVal = maxV > minV ? (val - minV) / (maxV - minV) : 0.5;
          const dotR = 2 + tVal * 6;
          return (
            <circle
              key={i}
              cx={dot.x}
              cy={dot.y}
              r={dotR}
              fill={dotColor}
              opacity={0.85}
              style={{
                transformOrigin: `${dot.x}px ${dot.y}px`,
                transform: isHovered ? "scale(1.8)" : "scale(1)",
                transition: "transform 0.2s ease, r 0.3s ease",
              }}
            />
          );
        })}

        {/* Hover */}
        {tooltip && (
          <>
            <line x1={tooltip.x} y1={PAD.t} x2={tooltip.x} y2={PAD.t + gH}
              stroke="var(--border2)" strokeWidth="1" strokeDasharray="4 3"/>
            <circle cx={tooltip.x} cy={tooltip.y} r={tooltip.isBuy ? 5.5 : 3.5}
              fill={tooltip.isBuy
                ? (currentPrice >= tooltip.data.buyPrice ? "#5aad27" : C.red)
                : greyLine}
              stroke="#000" strokeWidth="1.5"/>
          </>
        )}
      </svg>

      {/* Tooltip */}
      {tooltip && (() => {
        const dotColor = tooltip.isBuy
          ? (currentPrice >= tooltip.data.buyPrice ? "#5aad27" : C.red)
          : "#222";
        return (
          <div style={{
            position: "absolute",
            left: Math.min(tooltip.x + 14, dims.w - 175),
            top:  Math.max(tooltip.y - 85, 4),
            background: "#0a0a0a",
            border: `1px solid ${tooltip.isBuy ? dotColor : "#222"}`,
            borderRadius: 8, padding: "10px 14px",
            pointerEvents: "none", fontFamily: "Montserrat,sans-serif", zIndex: 10,
            
          }}>
            <div style={{ fontSize: 9, color:"var(--text3)", letterSpacing: "0.2em", marginBottom: 7 }}>
              {formatDate(tooltip.date)}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color:"var(--text2)", marginBottom: tooltip.isBuy ? 6 : 0 }}>
              {fmt$(tooltip.price)}
            </div>
            {tooltip.isBuy && (
              <>
                <div style={{ width: "100%", height: 1, background: "#1a1a1a", margin: "6px 0" }}/>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 3 }}>
                  <span style={{ fontSize: 10, color:"var(--text2)" }}>Purchased</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: dotColor }}>
                    {tooltip.data.shares.toFixed(4)} shares
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16, marginBottom: 3 }}>
                  <span style={{ fontSize: 10, color:"var(--text2)" }}>Invested</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color:"var(--text2)" }}>
                    {fmt$(tooltip.data.dollars)}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
                  <span style={{ fontSize: 10, color:"var(--text2)" }}>Buy price</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color:"var(--text2)" }}>
                    {fmt$(tooltip.data.buyPrice)}
                  </span>
                </div>
                {tooltip.data.count > 1 && (
                  <div style={{ fontSize: 9, color:"var(--text3)", marginTop: 4 }}>
                    {tooltip.data.count} транзshares
                  </div>
                )}
              </>
            )}
          </div>
        );
      })()}
    </div>
  );
}

export default PriceTradeChart;
